<template>
  <div class="bbm-dir">

    <!-- Sub-nav journey pillars -->
    <div class="bbm-subnav">
      <div class="bbm-subnav__inner">
        <span
          v-for="pillar in pillars"
          :key="pillar.key"
          :class="['bbm-subnav__link', activePillar === pillar.key ? 'active' : '']"
          @click="activePillar = pillar.key"
        >{{ pillar.label }}</span>
      </div>
    </div>

    <main class="bbm-dir__main">

      <!-- Hero -->
      <section class="bbm-hero">
        <div class="bbm-hero__content">
          <h1 class="bbm-hero__title">{{ currentPillar.heroTitle }}</h1>
          <p class="bbm-hero__sub">{{ currentPillar.heroSub }}</p>
        </div>
        <div class="bbm-hero__overlay"></div>
      </section>

      <!-- Premium Partners -->
      <section class="bbm-section">
        <h2 class="bbm-section__title tw-flex items-center gap-2" style="margin-bottom: 24px;">
          <span class="material-symbols-outlined bbm-section__icon">star</span>
          Premium Partners
        </h2>
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div v-for="n in 3" :key="n" class="bbm-skeleton-card">
            <div class="bbm-skeleton bbm-skeleton--image-lg"></div>
            <div style="padding: 24px;">
              <div class="bbm-skeleton bbm-skeleton--title"></div>
              <div class="bbm-skeleton bbm-skeleton--text"></div>
              <div class="bbm-skeleton bbm-skeleton--text" style="width: 60%"></div>
              <div class="bbm-skeleton bbm-skeleton--btn" style="margin-top: 24px;"></div>
            </div>
          </div>
        </div>
        <div v-else-if="pillarPartners.length" class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            v-for="partner in pillarPartners"
            :key="partner.id"
            class="bbm-featured-card group"
            @click="goToPartner(partner)"
          >
            <div class="bbm-featured-card__image">
              <img :src="actorImage(partner)" :alt="partner.name"/>
              <span class="bbm-badge bbm-badge--member">
                <span class="material-symbols-outlined" style="font-size:12px;">verified</span>
                PARTNER
              </span>
            </div>
            <div class="bbm-featured-card__body">
              <div class="tw-flex justify-between items-start" style="margin-bottom: 8px;">
                <h3 class="bbm-featured-card__title">{{ partner.name }}</h3>
              </div>
              <p class="bbm-featured-card__desc">{{ actorDescription(partner) }}</p>
              <button class="bbm-btn-accent">View Full Profile</button>
            </div>
          </div>
        </div>
        <div v-else class="bbm-empty">
          <span class="material-symbols-outlined bbm-empty__icon">star</span>
          <p>No premium partners available yet.</p>
        </div>
      </section>

      <!-- Premium Hubs -->
      <section class="bbm-section">
        <div class="tw-flex justify-between items-center" style="margin-bottom: 32px;">
          <h2 class="bbm-section__title tw-flex items-center gap-2">
            <span class="material-symbols-outlined bbm-section__icon">apartment</span>
            Premium Hubs
          </h2>
          <!--          <span class="bbm-link cursor-pointer" @click="$router.push('/login')">
                      View all hubs <span class="material-symbols-outlined" style="font-size:14px; vertical-align: middle;">arrow_forward</span>
                    </span>-->
        </div>
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div v-for="n in 3" :key="n" class="bbm-skeleton-card">
            <div class="bbm-skeleton bbm-skeleton--image-lg"></div>
            <div style="padding: 24px;">
              <div class="bbm-skeleton bbm-skeleton--title"></div>
              <div class="bbm-skeleton bbm-skeleton--text"></div>
              <div class="bbm-skeleton bbm-skeleton--text" style="width: 60%"></div>
              <div class="bbm-skeleton bbm-skeleton--btn" style="margin-top: 24px;"></div>
            </div>
          </div>
        </div>
        <div v-else-if="pillarHubs.length" class="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div
            v-for="hub in pillarHubs"
            :key="hub.id"
            class="bbm-featured-card group"
            @click="goToHub(hub)"
          >
            <div class="bbm-featured-card__image">
              <img :src="actorImage(hub)" :alt="hub.name"/>
              <span class="bbm-badge bbm-badge--member">
                <span class="material-symbols-outlined" style="font-size:12px;">apartment</span>
                HUB
              </span>
            </div>
            <div class="bbm-featured-card__body">
              <div class="tw-flex justify-between items-start" style="margin-bottom: 8px;">
                <h3 class="bbm-featured-card__title">{{ hub.name }}</h3>
              </div>
              <p class="bbm-featured-card__desc">{{ actorDescription(hub) }}</p>
              <button class="bbm-btn-accent">View Hub</button>
            </div>
          </div>
        </div>
        <div v-else class="bbm-empty">
          <span class="material-symbols-outlined bbm-empty__icon">apartment</span>
          <p>No premium hubs available yet.</p>
        </div>
      </section>

      <!-- Members -->
      <section class="bbm-section">
        <div class="tw-flex justify-between items-center" style="margin-bottom: 24px;">
          <h2 class="bbm-section__title">Ecosystem Members</h2>
          <div class="bbm-search">
            <span class="material-symbols-outlined bbm-search__icon">search</span>
            <input v-model="searchQuery" class="bbm-search__input" placeholder="Search..." type="text"/>
          </div>
        </div>

        <!-- Loading skeleton -->
        <div v-if="loading" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div v-for="n in 6" :key="n" class="bbm-skeleton-card" style="padding: 24px;">
            <div class="tw-flex items-center gap-4" style="margin-bottom: 12px;">
              <div class="bbm-skeleton" style="width:48px;height:48px;border-radius:9999px;flex-shrink:0;"></div>
              <div class="bbm-skeleton bbm-skeleton--title" style="margin:0;flex:1;"></div>
            </div>
            <div class="bbm-skeleton bbm-skeleton--text"></div>
            <div class="bbm-skeleton bbm-skeleton--text" style="width:70%"></div>
          </div>
        </div>

        <!-- Grid -->
        <template v-else-if="sortedActors.length">
          <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <div
              v-for="member in visibleActors"
              :key="member.id || member.name"
              class="bbm-provider-card"
              @click="goToLogin(member)"
            >
              <div class="bbm-provider-card__status-bar">
                <span v-if="member.claimed" class="bbm-verified-badge">
                  <span class="material-symbols-outlined" style="font-size:13px;font-variation-settings:'FILL' 1,'wght' 400,'GRAD' 0,'opsz' 24;">check_circle</span>
                  Verified
                </span>
                <button v-else class="bbm-claim-btn" @click.stop="goToLogin(member)">
                  <span class="material-symbols-outlined" style="font-size:13px;">add_circle</span>
                  Claim
                </button>
              </div>
              <div class="tw-flex items-center gap-4" style="margin-bottom: 12px;">
                <div class="bbm-provider-card__icon-wrap">
                  <img :src="actorImage(member)" :alt="member.name" class="bbm-provider-card__img"/>
                </div>
                <div style="flex: 1; min-width: 0;">
                  <h4 class="bbm-provider-card__name">{{ member.name }}</h4>
                </div>
              </div>
              <p class="bbm-provider-card__desc">{{ actorDescription(member) }}</p>
            </div>
          </div>

          <!-- Infinite scroll sentinel -->
          <div ref="sentinel" class="bbm-sentinel"></div>
        </template>

        <div v-else class="bbm-empty">
          <span class="material-symbols-outlined bbm-empty__icon">{{ searchQuery ? 'search_off' : 'group' }}</span>
          <p>{{ searchQuery ? `No results for "${searchQuery}"` : 'No members available yet.' }}</p>
        </div>
      </section>

    </main>

    <SimplifiedFooterBrussels/>
  </div>
</template>

<script lang="ts">
  import { defineComponent } from 'vue'
  import { fetchActors } from '@/api/actors'
  import { getDefaultFallbackImageUrl } from '@/util/helpers'
  import SimplifiedFooterBrussels from '@/components/SimplifiedFooter/SimplifiedFooterBrussels.vue'
  import { trackBECICta, trackBECITabSwitch, trackBECICard, createScrollDepthTracker } from '@/util/useBECITracking.js'

  const PILLARS = [
    {
      key: 'start',
      portfolioEM: 'aAVbzj9Vmr',
      portfolioHubs: 'L0V4vPWkb7',
      portfolioPartners: 'lMk1Bb6xRK',
      label: 'Start your company',
      heroTitle: 'From the initial idea to start-up, we\'re here to help you launch your business in Brussels.',
      heroSub: 'Everything you need to launch your venture in Brussels.',
    },
    {
      key: 'expand',
      portfolioEM: 'LWn2D2rxZP',
      portfolioHubs: 'E3naM99n1M',
      portfolioPartners: 'oBnPBErxq1',
      label: 'Expanding your Business',
      heroTitle: 'Expanding your Business',
      heroSub: 'Strategy, financing, location - we\'re there every step of the way',
    },
    {
      key: 'abroad',
      portfolioEM: 'mJn5vWzkor',
      portfolioHubs: 'b3koW7QkWr',
      portfolioPartners: 'rRnWRbdVzb',
      label: 'Grow your business abroad',
      heroTitle: 'Grow your business abroad.',
      heroSub: 'Go international and develop partnerships or exports.',
    },
    {
      key: 'transform',
      portfolioEM: '14n808Zn20',
      portfolioHubs: '4AxMpZNk9j',
      portfolioPartners: 'epVAD5mx09',
      label: 'Transform your business',
      heroTitle: 'Transform your business.',
      heroSub: 'More sustainable, more innovative and more digital, step by step.',
    },
  ]

  export default defineComponent({
    name: 'StaticHomeBrusselsJourneys',
    components: { SimplifiedFooterBrussels },
    data() {
      return {
        activePillar: 'start' as string,
        searchQuery: '' as string,
        pillars: PILLARS,
        actors: [] as any[],
        pillarHubs: [] as any[],
        pillarPartners: [] as any[],
        loading: false,
        visibleCount: 12,
        _observer: null as IntersectionObserver | null,
        _scrollTracker: null as { start: () => void; stop: () => void } | null,
      }
    },
    computed: {
      currentPillar(): any {
        return this.pillars.find((p: any) => p.key === this.activePillar) || this.pillars[0]
      },
      filteredActors(): any[] {
        if (!this.searchQuery.trim()) return this.actors
        const q = this.searchQuery.toLowerCase()
        return this.actors.filter((a: any) =>
          (a.name || '').toLowerCase().includes(q) ||
          (a.short_description || a.activities_description || '').toLowerCase().includes(q)
        )
      },
      sortedActors(): any[] {
        return [...this.filteredActors].sort((a: any, b: any) =>
          (b.claimed ? 1 : 0) - (a.claimed ? 1 : 0)
        )
      },
      visibleActors(): any[] {
        return this.sortedActors.slice(0, this.visibleCount)
      },
    },
    mounted() {
      this._scrollTracker = createScrollDepthTracker('BECI Journeys')
      this._scrollTracker?.start()
      const pillarParam = this.$route?.query?.pillar as string | undefined
      if (pillarParam && this.pillars.find((p: any) => p.key === pillarParam)) {
        this.activePillar = pillarParam
      }
      this.loadActors()
      this.setupObserver()
    },
    unmounted() {
      this._scrollTracker?.stop()
      this._observer?.disconnect()
    },
    watch: {
      activePillar(newVal: string) {
        const pillar = this.pillars.find((p: any) => p.key === newVal)
        if (pillar) trackBECITabSwitch('Journeys', pillar.label)
        this.visibleCount = 12
        this.loadActors()
      },
      searchQuery() {
        this.visibleCount = 12
      },
      visibleActors() {
        this.$nextTick(() => this.setupObserver())
      },
    },
    methods: {
      goToLogin(actor: any) {
        trackBECICard('Journey Member', actor.name)
        if (this.$store.getters.isLoggedIn) {
          this.$router.push(`/actors/${actor.id}`)
        } else {
          this.$router.push({ path: '/login', query: { external: 'view-actor', actor_id: actor.id, actor_name: actor.name } })
        }
      },
      goToHub(hub: any) {
        trackBECICard('Journey Hub', hub.name)
        this.$router.push(`/hubs-brussels/${hub.id}`)
      },
      goToPartner(partner: any) {
        trackBECICard('Journey Partner', partner.name)
        this.$router.push(`/actors/${partner.id}`)
      },
      setupObserver() {
        this._observer?.disconnect()
        const sentinel = this.$refs.sentinel as HTMLElement | undefined
        if (!sentinel) return
        this._observer = new IntersectionObserver((entries) => {
          if (entries[0].isIntersecting && this.visibleCount < this.sortedActors.length) {
            this.visibleCount += 12
          }
        }, { rootMargin: '200px' })
        this._observer.observe(sentinel)
      },
      async loadActors() {
        const pillar: any = this.currentPillar
        this.loading = true
        this.actors = []
        this.pillarHubs = []
        this.pillarPartners = []
        const safe = (promise: Promise<any>) => promise.catch(() => ({ data: [] }))
        const [emResult, hubsResult, partnersResult] = await Promise.all([
          pillar.portfolioEM ? safe(fetchActors({ portfolio: pillar.portfolioEM, limit: 60 })) : Promise.resolve({ data: [] }),
          pillar.portfolioHubs ? safe(fetchActors({ portfolio: pillar.portfolioHubs, limit: 3 })) : Promise.resolve({ data: [] }),
          pillar.portfolioPartners ? safe(fetchActors({ portfolio: pillar.portfolioPartners, limit: 3 })) : Promise.resolve({ data: [] }),
        ])
        this.actors = (emResult as any).data || []
        this.pillarHubs = (hubsResult as any).data || []
        this.pillarPartners = (partnersResult as any).data || []
        this.loading = false
      },
      actorImage(actor: any): string {
        return actor.featured_image_url || actor.website_screenshot || getDefaultFallbackImageUrl()
      },
      actorDescription(actor: any): string {
        return actor.short_description || actor.activities_description || ''
      },
    },
  })
</script>

<style scoped lang="scss">
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;600;700&family=Inter:wght@400;500;600;700&display=swap');
  @import url('https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:wght,FILL@100..700,0..1&display=swap');

  $bbm-primary: #001e40;
  $bbm-primary-container: #003366;
  $bbm-accent: #ff6f31;
  $bbm-accent-dark: #802a00;
  $bbm-secondary: #00677f;
  $bbm-secondary-container: #00ccf9;
  $bbm-surface: #f7f9fb;
  $bbm-muted: #43474f;

  .material-symbols-outlined {
    font-variation-settings: 'FILL' 0, 'wght' 400, 'GRAD' 0, 'opsz' 24;
  }

  .bbm-dir {
    font-family: 'Inter', sans-serif;
    background: $bbm-surface;
    color: #191c1e;
    overflow-x: hidden;
    min-height: 100vh;
  }

  /* ── Sub-nav ── */
  .bbm-subnav {
    position: sticky;
    z-index: 40;
    background: white;
    border-bottom: 1px solid #f1f5f9;
    overflow-x: auto;
    white-space: nowrap;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .bbm-subnav__inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px;
    display: flex;
    gap: 32px;
    height: 48px;
    align-items: center;
  }

  .bbm-subnav__link {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.875rem;
    font-weight: 500;
    color: #64748b;
    height: 100%;
    display: inline-flex;
    align-items: center;
    border-bottom: 2px solid transparent;
    cursor: pointer;
    transition: color 0.15s, border-color 0.15s;
    white-space: nowrap;

    &:hover {
      color: $bbm-primary-container;
    }

    &.active {
      color: $bbm-primary-container;
      font-weight: 700;
      border-bottom-color: $bbm-primary-container;
    }
  }

  /* ── Main ── */
  .bbm-dir__main {
    max-width: 1280px;
    margin: 0 auto;
    padding: 0 32px 80px;
  }

  /* ── Hero ── */
  .bbm-hero {
    position: relative;
    border-radius: 12px;
    overflow: hidden;
    background: $bbm-primary-container;
    padding: 48px;
    margin: 48px 0;
    color: white;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(to right, rgba(0, 30, 64, 0.6), transparent);
      pointer-events: none;
    }
  }

  .bbm-hero__content {
    position: relative;
    z-index: 1;
    max-width: 600px;
  }

  .bbm-hero__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: clamp(1.75rem, 3vw, 3rem);
    font-weight: 700;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: white;
    margin: 0 0 16px;
  }

  .bbm-hero__sub {
    font-size: 1.125rem;
    line-height: 1.6;
    color: #cbd5e1;
    margin: 0;
  }

  .bbm-hero__overlay {
    display: none;
  }

  /* ── Section ── */
  .bbm-section {
    margin-bottom: 64px;
  }

  .bbm-section__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.75rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0;
    line-height: 1.2;
  }

  /* ── Search ── */
  .bbm-search {
    display: flex;
    align-items: center;
    gap: 8px;
    border: 1px solid #e2e8f0;
    border-radius: 8px;
    padding: 6px 12px;
    background: white;
  }

  .bbm-search__icon {
    color: #94a3b8;
    font-size: 1rem;
  }

  .bbm-search__input {
    border: none;
    outline: none;
    font-size: 0.875rem;
    width: 192px;
    background: transparent;
    color: #191c1e;

    &::placeholder {
      color: #94a3b8;
    }
  }

  /* ── Section icon ── */
  .bbm-section__icon {
    color: $bbm-secondary-container;
    font-size: 1.5rem;
  }

  /* ── Links ── */
  .bbm-link {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 0.875rem;
    font-weight: 700;
    color: $bbm-primary-container;
    text-decoration: none;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    transition: gap 0.15s, text-decoration 0.15s;

    &:hover {
      text-decoration: underline;
    }
  }

  /* ── Featured cards (hubs & partners) ── */
  .bbm-featured-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
    cursor: pointer;
    transition: box-shadow 0.25s;

    &:hover {
      box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
    }
  }

  .bbm-featured-card__image {
    height: 256px;
    position: relative;
    overflow: hidden;

    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      transition: transform 0.5s;

      .bbm-featured-card:hover & {
        transform: scale(1.04);
      }
    }
  }

  .bbm-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.625rem;
    font-weight: 700;
    letter-spacing: 0.04em;

    &--member {
      position: absolute;
      top: 16px;
      left: 16px;
      background: $bbm-primary-container;
      color: white;
      padding: 4px 8px;
      border-radius: 2px;
    }
  }

  .bbm-featured-card__body {
    padding: 24px;
  }

  .bbm-featured-card__title {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1.25rem;
    font-weight: 600;
    color: $bbm-primary;
    margin: 0;
  }

  .bbm-tag {
    font-size: 0.75rem;
    font-weight: 700;
    color: $bbm-secondary;
    background: rgba(0, 204, 249, 0.1);
    padding: 4px 8px;
    border-radius: 4px;
    white-space: nowrap;
  }

  .bbm-featured-card__desc {
    font-size: 0.9375rem;
    line-height: 1.5;
    color: #475569;
    margin: 0 0 24px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  .bbm-btn-accent {
    width: 100%;
    padding: 12px 0;
    background: $bbm-accent;
    color: white;
    font-weight: 700;
    font-size: 0.9375rem;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    transition: background 0.2s, transform 0.1s;

    &:hover {
      background: $bbm-accent-dark;
    }

    &:active {
      transform: scale(0.98);
    }
  }

  /* ── Skeleton image sizes ── */
  .bbm-skeleton--image-lg {
    height: 256px;
    border-radius: 0;
    margin-bottom: 0;
  }

  .bbm-skeleton--btn {
    height: 36px;
    width: 100%;
    border-radius: 8px;
  }

  /* ── Member cards ── */
  .bbm-provider-card {
    background: white;
    padding: 24px;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    cursor: pointer;
    transition: border-color 0.2s;

    &:hover {
      border-color: $bbm-secondary-container;
    }
  }

  .bbm-provider-card__status-bar {
    margin-bottom: 12px;
    min-height: 26px;
    text-align: right;
  }

  .bbm-verified-badge {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 500;
    color: #1d4ed8;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    padding: 3px 9px;
    border-radius: 9999px;
  }

  .bbm-claim-btn {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 0.75rem;
    font-weight: 700;
    font-family: inherit;
    color: #166534;
    background: #dcfce7;
    border: 1px solid #bbf7d0;
    padding: 3px 9px;
    border-radius: 9999px;
    cursor: pointer;
    transition: background 0.15s;

    &:hover {
      background: #bbf7d0;
    }
  }

  .bbm-provider-card__icon-wrap {
    width: 48px;
    height: 48px;
    background: #f1f5f9;
    border-radius: 9999px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .bbm-provider-card__img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 9999px;
  }

  .bbm-provider-card__name {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 1rem;
    font-weight: 600;
    color: #191c1e;
    margin: 0;
  }

  .bbm-provider-card__desc {
    font-size: 0.875rem;
    line-height: 1.5;
    color: #475569;
    margin: 0;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }

  /* ── Infinite scroll sentinel ── */
  .bbm-sentinel {
    height: 1px;
    margin-top: 40px;
  }

  /* ── Empty ── */
  .bbm-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 64px 0;
    color: #94a3b8;
    gap: 12px;
  }

  .bbm-empty__icon {
    font-size: 3rem;
    opacity: 0.5;
  }

  /* ── Skeleton ── */
  @keyframes shimmer {
    0% {
      background-position: -400px 0;
    }
    100% {
      background-position: 400px 0;
    }
  }

  .bbm-skeleton-card {
    background: white;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    overflow: hidden;
  }

  .bbm-skeleton {
    background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
    background-size: 400px 100%;
    animation: shimmer 1.4s infinite;
    border-radius: 4px;
  }

  .bbm-skeleton--title {
    height: 20px;
    width: 70%;
    margin-bottom: 12px;
  }

  .bbm-skeleton--text {
    height: 14px;
    width: 90%;
    margin-bottom: 8px;
  }

  @media (max-width: 768px) {
    .bbm-dir__main {
      padding: 0 16px 64px;
    }
    .bbm-hero {
      padding: 32px 24px;
    }
    .bbm-subnav__inner {
      padding: 0 16px;
    }
  }
</style>
